import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    GroupTableCustomSortComparators,
    normalizeGroupTableFilterText,
    toGroupTableMetrics,
} from '@/components/group-tables/group-table-view-model-primitives';
import { sortGroupTableValuesByDirection } from '@/components/group-tables/use-group-table-view-model-core';
import { useGroupTableDomainViewModel } from '@/components/group-tables/use-group-table-domain-view-model';
import { SortDirection } from '@/enums/sort';
import {
    readStoredBrandsBrandFilter,
    readStoredBrandsNameFilter,
    readStoredBrandsSort,
    writeStoredBrandsBrandFilter,
    writeStoredBrandsNameFilter,
    writeStoredBrandsSort,
} from '@/context/reviews-page-transport.helpers';

export type BrandsTableData = {
    id: BrandGroupEntity['id'];
    name: BrandGroupEntity['name'];
    brandNames: BrandGroupEntity['brandNames'];
    brandCount: BrandGroupEntity['brandCount'];
    productGroupIds: BrandGroupEntity['productGroupIds'];
    productCount: BrandGroupEntity['productCount'];
    productGroupCount: BrandGroupEntity['productGroupCount'];
    reviewCount: number;
    answerCount: number;
    answerPercent: number | null;
    averageRating: number | null;
    nps: number | null;
    aiAnalysisCount: number;
    manage: boolean;
};

const BRANDS_TABLE_SORT_COMPARATORS: GroupTableCustomSortComparators<BrandsTableData> = {
    name: (leftBrandGroup, rightBrandGroup, currentDirection) =>
        sortGroupTableValuesByDirection(leftBrandGroup.name, rightBrandGroup.name, currentDirection),
    brandCount: (leftBrandGroup, rightBrandGroup, currentDirection) =>
        sortGroupTableValuesByDirection(leftBrandGroup.brandCount, rightBrandGroup.brandCount, currentDirection),
    productGroupCount: (leftBrandGroup, rightBrandGroup, currentDirection) =>
        sortGroupTableValuesByDirection(
            leftBrandGroup.productGroupCount,
            rightBrandGroup.productGroupCount,
            currentDirection,
        ),
    productCount: (leftBrandGroup, rightBrandGroup, currentDirection) =>
        sortGroupTableValuesByDirection(leftBrandGroup.productCount, rightBrandGroup.productCount, currentDirection),
};

type UseBrandsTableViewModelArgs = {
    clientId: string;
    brandGroups: BrandGroupEntity[];
    brandsPage: number;
    brandsOnPage: number;
    setBrandsPage: (page: number) => void;
    setBrandsPageCount: (count: number) => void;
    setBrandsTotalRows: (count: number) => void;
};

export const useBrandsTableViewModel = ({
    clientId,
    brandGroups,
    brandsPage,
    brandsOnPage,
    setBrandsPage,
    setBrandsPageCount,
    setBrandsTotalRows,
}: UseBrandsTableViewModelArgs) => {
    const [brandFilter, setBrandFilter] = useState('');
    const hydratedClientIdRef = useRef<string | null>(null);
    const [isStorageHydrated, setIsStorageHydrated] = useState(false);
    const normalizedBrandFilter = normalizeGroupTableFilterText(brandFilter);

    const mapBrandGroupToTableData = useCallback(
        (brandGroup: BrandGroupEntity): BrandsTableData => ({
            id: brandGroup.id,
            name: brandGroup.name,
            brandNames: brandGroup.brandNames,
            brandCount: brandGroup.brandCount,
            productGroupIds: brandGroup.productGroupIds,
            productCount: brandGroup.productCount,
            productGroupCount: brandGroup.productGroupCount,
            ...toGroupTableMetrics(brandGroup),
            manage: true,
        }),
        [],
    );

    const {
        data: availableBrandGroups,
        nameFilter,
        setNameFilter,
        sort,
        filteredAndSortedData: filteredAndSortedBrandGroups,
        pagedData: pagedBrandGroups,
        onSortChanged,
    } = useGroupTableDomainViewModel<BrandGroupEntity, BrandsTableData>({
        entities: brandGroups,
        mapEntityToData: mapBrandGroupToTableData,
        page: brandsPage,
        rowsOnPage: brandsOnPage,
        setPage: setBrandsPage,
        setPageCount: setBrandsPageCount,
        setTotalRows: setBrandsTotalRows,
        filterData: (brandGroup, normalizedNameFilter) => {
            const matchesName = normalizedNameFilter
                ? brandGroup.name.toLowerCase().includes(normalizedNameFilter)
                : true;
            const matchesBrands = normalizedBrandFilter
                ? brandGroup.brandNames.some((brandName) => brandName.toLowerCase().includes(normalizedBrandFilter))
                : true;

            return matchesName && matchesBrands;
        },
        sortComparators: BRANDS_TABLE_SORT_COMPARATORS,
    });

    useEffect(() => {
        if (hydratedClientIdRef.current === clientId) {
            return;
        }

        hydratedClientIdRef.current = clientId;
        setIsStorageHydrated(false);
        setNameFilter(readStoredBrandsNameFilter(clientId));
        setBrandFilter(readStoredBrandsBrandFilter(clientId));

        const storedSort = readStoredBrandsSort(clientId);
        if (storedSort.sortKey && storedSort.sortOrder) {
            onSortChanged(storedSort.sortKey as keyof BrandsTableData, storedSort.sortOrder);
        } else if (sort.sortKey && sort.sortOrder) {
            onSortChanged(sort.sortKey as keyof BrandsTableData, SortDirection.NONE);
        }
        setIsStorageHydrated(true);
    }, [clientId, onSortChanged, setNameFilter, sort.sortKey, sort.sortOrder]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredBrandsNameFilter(clientId, nameFilter);
    }, [clientId, isStorageHydrated, nameFilter]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredBrandsBrandFilter(clientId, brandFilter);
    }, [brandFilter, clientId, isStorageHydrated]);

    useEffect(() => {
        if (!isStorageHydrated) {
            return;
        }

        writeStoredBrandsSort(clientId, sort);
    }, [clientId, isStorageHydrated, sort.sortKey, sort.sortOrder]);

    return {
        availableBrandGroups,
        nameFilter,
        setNameFilter,
        brandFilter,
        setBrandFilter,
        sort,
        filteredAndSortedBrandGroups,
        pagedBrandGroups,
        onSortChanged,
    };
};
